import { Moment, MOMENT_SLUGS, PAGE_MOMENTS, SectionKey, PORTAL_PAGES, HeadingSize } from '@/constants'
import type {
  AnnouncementBarData,
  ConfigurablePage,
  MomentKey,
  NavigationalBlocksData,
  PortalSection,
  PortalSettings,
  SectionsConfig,
  WelcomeMessageData,
} from '@/types'
import { withMomentCard } from './momentCard'

const DEFAULT_NAV_LABELS: Required<NavigationalBlocksData> = {
  home_label: 'Home',
  orders_label: 'Orders',
  subscriptions_label: 'Subscriptions',
  address_payment_label: 'Account',
  log_out_label: 'Log Out',
  show_icons: true,
  show_address_payment: true,
}

const DEFAULT_WELCOME: WelcomeMessageData = {
  heading: 'Welcome back, {first_name}',
  text_size: HeadingSize.Medium,
}

function parseConfig(portalSettings: PortalSettings | undefined): SectionsConfig | undefined {
  let config = portalSettings?.sections_config as any
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return undefined
    }
  }
  return config as SectionsConfig | undefined
}

// Missing or unknown moment_status falls back to the `default` moment.
export function resolveMoment(session: any): MomentKey {
  return MOMENT_SLUGS[Number(session?.moment_status)] ?? MOMENT_SLUGS[Moment.None]
}

export function pageHasMoments(page: ConfigurablePage): boolean {
  return page in PAGE_MOMENTS
}

function momentForPage(page: ConfigurablePage, moment: MomentKey): MomentKey {
  return PAGE_MOMENTS[page]?.includes(moment) ? moment : MOMENT_SLUGS[Moment.None]
}

function sectionsForPage(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection[] {
  const saved = parseConfig(portalSettings)?.[page]
  if (Array.isArray(saved)) {
    return saved
  }
  if (!saved || !pageHasMoments(page)) {
    return []
  }
  const variant = saved[momentForPage(page, moment)] ?? saved[MOMENT_SLUGS[Moment.None]]
  return Array.isArray(variant) ? variant : []
}

export function getHomeSections(portalSettings: PortalSettings | undefined, moment: MomentKey): PortalSection[] {
  return withMomentCard(sectionsForPage(portalSettings, PORTAL_PAGES.Home, moment), moment)
}

export function getPageSections(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection[] {
  return sectionsForPage(portalSettings, page, moment)
}

export function findSection<T = Record<string, any>>(sections: PortalSection[], key: SectionKey): T | undefined {
  return sections.find(s => s.key === key)?.data as T | undefined
}

// Chrome is rendered by OmpLayout; everything else goes through the registry.
const CHROME_SECTION_KEYS: SectionKey[] = [
  SectionKey.Header,
  SectionKey.AnnouncementBar,
  SectionKey.WelcomeMessage,
  SectionKey.NavigationalBlocks,
  SectionKey.Footer,
]

function getContentSections(portalSettings: PortalSettings | undefined, page: ConfigurablePage): PortalSection[] {
  return getPageSections(portalSettings, page).filter(s => !CHROME_SECTION_KEYS.includes(s.key))
}

// Guarantees the page's list section exists; a config omitting it would render empty.
export function getListPageSections(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  listKey: SectionKey
): PortalSection[] {
  const sections = getContentSections(portalSettings, page)
  if (sections.some(s => s.key === listKey)) {
    return sections
  }
  return [{ key: listKey, data: {}, is_visible: true }, ...sections]
}

export function getRenderedSections(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection[] {
  switch (page) {
    case PORTAL_PAGES.Home:
      return getHomeSections(portalSettings, moment)
    case PORTAL_PAGES.Subscriptions:
      return getListPageSections(portalSettings, page, SectionKey.SubscriptionList)
    case PORTAL_PAGES.Orders:
      return getListPageSections(portalSettings, page, SectionKey.OrderList)
    default:
      return getPageSections(portalSettings, page, moment)
  }
}

export function resolveNavLabels(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = PORTAL_PAGES.Home,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): Required<NavigationalBlocksData> {
  const data =
    findSection<NavigationalBlocksData>(sectionsForPage(portalSettings, page, moment), SectionKey.NavigationalBlocks) ??
    findSection<NavigationalBlocksData>(getHomeSections(portalSettings, moment), SectionKey.NavigationalBlocks)
  return { ...DEFAULT_NAV_LABELS, ...(data ?? {}) }
}

// Returns the current page's announcement_bar section (with is_visible), or undefined.
export function resolveAnnouncement(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = PORTAL_PAGES.Home,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection<AnnouncementBarData> | undefined {
  return sectionsForPage(portalSettings, page, moment).find(s => s.key === SectionKey.AnnouncementBar) as
    PortalSection<AnnouncementBarData> | undefined
}

// Enable Footer ships with the editor's footer panel; until then no config carries
// a footer entry, and an absent one means show it.
export function isFooterVisible(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = PORTAL_PAGES.Home,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): boolean {
  return sectionsForPage(portalSettings, page, moment).find(s => s.key === SectionKey.Footer)?.is_visible !== false
}

// Home only — the editor strips welcome_message from every other page.
export function resolveWelcome(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = PORTAL_PAGES.Home,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): { data: WelcomeMessageData; visible: boolean; uid?: string } {
  if (page !== PORTAL_PAGES.Home) {
    return { data: DEFAULT_WELCOME, visible: false }
  }
  const section = getHomeSections(portalSettings, moment).find(s => s.key === SectionKey.WelcomeMessage)
  return {
    data: { ...DEFAULT_WELCOME, ...(section?.data ?? {}) },
    visible: section?.is_visible !== false,
    uid: section?.uid,
  }
}

export function resolveSectionMeta(
  portalSettings: PortalSettings | undefined,
  key: SectionKey,
  page: ConfigurablePage = PORTAL_PAGES.Home,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection | undefined {
  return (
    sectionsForPage(portalSettings, page, moment).find(s => s.key === key) ??
    getHomeSections(portalSettings, moment).find(s => s.key === key)
  )
}
